import { useState } from 'react'

import { Box, BoxList, Button, Text } from '@factorialco/deprecated-design-system'
import { AtsFileFragment } from 'generated/resources/graphql'
import moment from 'moment'

import DocumentType from 'modules/core/components/DocumentType'
import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useDeleteAtsFile } from 'modules/ats/hooks/files/useDeleteAtsFile'

interface Props {
  applicationFile: AtsFileFragment
}

export const ApplicationFileItem = ({ applicationFile }: Props) => {
  const { createdAt, authorFullName, file } = applicationFile
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const { deleteAtsFile, loading: deletingFile } = useDeleteAtsFile(applicationFile)

  const onDelete = async () => {
    const result = await deleteAtsFile()
    const handler = handleResult(result, () => null)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handler
  }

  const formattedCreatedAt = moment(createdAt).format('ll')
  const label = !!authorFullName
    ? i18n.t('ats.candidates.files.application_files.file_info', {
        created_at: formattedCreatedAt,
        hiring_manager: authorFullName,
      })
    : formattedCreatedAt

  return (
    <BoxList.Item>
      <Box alignItems="center" flexGrow flexDirection="row" gap="s16" minWidth="s0">
        <DocumentType mimetype={file.contentType} />
        <Box overflowX="hidden">
          <Text.Normal weight="medium" ellipsis overflowWrap>
            {file.filename}
          </Text.Normal>
          <Box paddingTop="s4">
            <Text.Normal size="200" color="grey600">
              {label}
            </Text.Normal>
          </Box>
        </Box>
      </Box>

      <Box flexDirection="row" gap="s8" paddingLeft="s16">
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative
              type="secondary"
              aria-label={i18n.t('ats.candidates.files.application_files.more_actions')}
              iconName="ellipsis"
            />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end" disablePortal>
            <PopoverMenu.HrefOption
              href={file.url}
              target="_blank"
              download
              title={i18n.t('documents.download')}
            />
            <PopoverMenu.TouchableOption
              onClick={() => setIsDeleteModalOpen(true)}
              title={i18n.t('documents.delete')}
              danger
            />
          </PopoverMenu.Content>
        </PopoverMenu>

        {isDeleteModalOpen && (
          <Modal
            title={i18n.t('documents.confirm_delete.title')}
            description={i18n.t('documents.confirm_delete.description')}
            illustration="input-field"
            onClose={() => setIsDeleteModalOpen(false)}
            onSubmit={onDelete}
            footer={
              <Button type="negative" disabled={deletingFile} submit>
                {i18n.t('documents.delete')}
              </Button>
            }
          />
        )}
      </Box>
    </BoxList.Item>
  )
}
